import { useQuery } from '@tanstack/react-query';
import { useApi, type PlatformReport } from '@/lib/api';
import { Card, ErrorText } from '@/components/ui';
import { CourseReportCards, QuizReportCards } from '@/pages/organizations/reports';

const PLAN_LABELS: Record<string, string> = {
  pilot: 'Pilot',
  campus_partner: 'Campus Partner',
  enterprise: 'Enterprise',
};

/**
 * Platform-wide reports for Nirvik's admins: totals, weekly sign-ups and
 * activity, organizations, and every course and quiz (all learners, whether
 * or not they belong to an org).
 */
export function AdminReportsPage() {
  const api = useApi();
  const report = useQuery({ queryKey: ['admin', 'reports'], queryFn: api.admin.reports });
  if (report.isPending) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (report.isError) return <ErrorText error={report.error} />;
  const r = report.data;
  const t = r.totals;

  return (
    <div className="space-y-8">
      <section className="space-y-4">
        <h2 className="text-lg font-semibold">At a glance</h2>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          <Stat
            label="People"
            value={t.users}
            hint={`${t.newUsers30.toLocaleString()} new in 30 days`}
          />
          <Stat label="Active learners" value={t.activeLearners30} hint="Last 30 days" />
          <Stat
            label="Organizations"
            value={t.topLevelOrgs}
            hint={`${t.orgs.toLocaleString()} including child organizations`}
          />
          <Stat label="Certificates issued" value={t.certificates} />
          <Stat
            label="Course enrollments"
            value={t.enrollments}
            hint={`${t.completions.toLocaleString()} completed`}
          />
          <Stat label="Quiz attempts" value={t.quizAttempts} />
          <Stat label="Published courses" value={t.publishedCourses} />
          <Stat
            label="Published quizzes"
            value={t.publishedQuizzes}
            hint="Not counting course assessments"
          />
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-lg font-semibold">Last 12 weeks</h2>
        <div className="grid gap-4 lg:grid-cols-2">
          <WeeklyBars
            title="New sign-ups"
            weeks={r.weekly.map((w) => ({ weekStart: w.weekStart, value: w.signups }))}
          />
          <WeeklyBars
            title="Active learners"
            weeks={r.weekly.map((w) => ({ weekStart: w.weekStart, value: w.activeLearners }))}
          />
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-lg font-semibold">Organizations</h2>
        <Organizations orgs={r.organizations} />
      </section>

      <section className="space-y-4">
        <h2 className="text-lg font-semibold">Courses</h2>
        {r.courses.length ? (
          <CourseReportCards courses={r.courses} />
        ) : (
          <Card>
            <p className="text-sm text-muted-foreground">No course enrollments yet.</p>
          </Card>
        )}
      </section>

      <section className="space-y-4">
        <h2 className="text-lg font-semibold">Quizzes</h2>
        {r.quizzes.length ? (
          <QuizReportCards quizzes={r.quizzes} />
        ) : (
          <Card>
            <p className="text-sm text-muted-foreground">No quiz attempts yet.</p>
          </Card>
        )}
      </section>
    </div>
  );
}

function Stat({ label, value, hint }: { label: string; value: number; hint?: string }) {
  return (
    <Card>
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="mt-1 text-3xl font-semibold tabular-nums">{value.toLocaleString()}</p>
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </Card>
  );
}

const shortDate = (iso: string) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });

/**
 * One measure per week as thin bars (a single series, so no legend: the title
 * names it). Hovering a bar shows its week and count; a table carries the same
 * numbers for screen readers.
 */
function WeeklyBars({
  title,
  weeks,
}: {
  title: string;
  weeks: { weekStart: string; value: number }[];
}) {
  const W = 600;
  const H = 140;
  const top = 28;
  const bottom = 2;
  const max = Math.max(1, ...weeks.map((w) => w.value));
  const slot = W / weeks.length;
  const barW = Math.min(28, slot - 8);
  const total = weeks.reduce((n, w) => n + w.value, 0);
  return (
    <Card>
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <h3 className="font-medium">{title}</h3>
        <p className="text-sm text-muted-foreground">{total.toLocaleString()} in 12 weeks</p>
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="h-auto w-full"
        role="img"
        aria-label={`${title} per week`}
      >
        <line
          x1={0}
          x2={W}
          y1={H - bottom}
          y2={H - bottom}
          className="stroke-border"
          strokeWidth={1}
        />
        {weeks.map((w, i) => {
          // At least 4px tall so a small count still shows (and the rounded top fits).
          const h = Math.max(4, ((H - top - bottom) * w.value) / max);
          const x = i * slot + (slot - barW) / 2;
          return (
            <g key={w.weekStart} className="group">
              {/* A taller hit area than the bar, for hovering small values. */}
              <rect
                x={i * slot}
                y={0}
                width={slot}
                height={H - bottom}
                className="fill-transparent"
              />
              {w.value > 0 && (
                <path
                  d={`M${x},${H - bottom} v${-(h - 4)} q0,-4 4,-4 h${barW - 8} q4,0 4,4 v${h - 4} z`}
                  className="fill-primary group-hover:opacity-80"
                />
              )}
              {w.value > 0 && i === weeks.length - 1 && (
                <text
                  x={x + barW / 2}
                  y={H - bottom - h - 4}
                  textAnchor="middle"
                  className="fill-foreground text-[22px] tabular-nums"
                >
                  {w.value}
                </text>
              )}
              <title>{`Week of ${shortDate(w.weekStart)}: ${w.value.toLocaleString()}`}</title>
            </g>
          );
        })}
      </svg>
      <div className="mt-1 flex justify-between text-xs text-muted-foreground">
        <span>Week of {shortDate(weeks[0]!.weekStart)}</span>
        <span>This week</span>
      </div>
      <table className="sr-only">
        <caption>{title} per week</caption>
        <tbody>
          {weeks.map((w) => (
            <tr key={w.weekStart}>
              <th scope="row">Week of {shortDate(w.weekStart)}</th>
              <td>{w.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function Organizations({ orgs }: { orgs: PlatformReport['organizations'] }) {
  if (!orgs.length)
    return (
      <Card>
        <p className="text-sm text-muted-foreground">No organizations yet.</p>
      </Card>
    );
  return (
    <Card>
      <p className="mb-3 text-sm text-muted-foreground">
        Top-level organizations, counting their child organizations.
      </p>
      {/* Phones: one row per organization. */}
      <ul className="divide-y divide-border sm:hidden">
        {orgs.map((o) => (
          <li key={o.id} className="py-3 text-sm">
            <p className="font-medium">{o.name}</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {PLAN_LABELS[o.planId] ?? o.planId} · {o.members.toLocaleString()} members ·{' '}
              {o.courseEnrollments.toLocaleString()} enrollments ({o.courseCompletions} completed) ·{' '}
              {o.quizAttempts.toLocaleString()} quiz attempts
            </p>
          </li>
        ))}
      </ul>
      <div className="hidden overflow-x-auto sm:block">
        <table className="w-full text-sm">
          <thead className="text-left text-muted-foreground">
            <tr>
              <th className="py-2 pr-4 font-medium">Organization</th>
              <th className="py-2 pr-4 font-medium">Plan</th>
              <th className="py-2 pr-4 text-right font-medium">Members</th>
              <th className="py-2 pr-4 text-right font-medium">Enrollments</th>
              <th className="py-2 pr-4 text-right font-medium">Completed</th>
              <th className="py-2 text-right font-medium">Quiz attempts</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {orgs.map((o) => (
              <tr key={o.id}>
                <td className="py-2 pr-4 font-medium">{o.name}</td>
                <td className="py-2 pr-4 text-muted-foreground">
                  {PLAN_LABELS[o.planId] ?? o.planId}
                </td>
                <td className="py-2 pr-4 text-right tabular-nums">{o.members.toLocaleString()}</td>
                <td className="py-2 pr-4 text-right tabular-nums">
                  {o.courseEnrollments.toLocaleString()}
                </td>
                <td className="py-2 pr-4 text-right tabular-nums">
                  {o.courseCompletions.toLocaleString()}
                </td>
                <td className="py-2 text-right tabular-nums">{o.quizAttempts.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
